import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { MessageSquare } from 'lucide-react';

const EMPLOYER_PREFS = [
  { key: 'sms_new_application', label: 'smsPrefs.newApplication', desc: 'smsPrefs.newApplicationDesc' },
  { key: 'sms_new_bid', label: 'smsPrefs.newBid', desc: 'smsPrefs.newBidDesc' },
  { key: 'sms_new_message', label: 'smsPrefs.newMessage', desc: 'smsPrefs.newMessageDesc' },
  { key: 'sms_interview_reminder', label: 'smsPrefs.interviewReminder', desc: 'smsPrefs.interviewReminderDesc' },
  { key: 'sms_interview_cancelled', label: 'smsPrefs.interviewCancelled', desc: 'smsPrefs.interviewCancelledDesc' },
];

const WORKER_PREFS = [
  { key: 'sms_new_message', label: 'smsPrefs.newMessage', desc: 'smsPrefs.newMessageDesc' },
  { key: 'sms_interview_invite', label: 'smsPrefs.interviewInvite', desc: 'smsPrefs.interviewInviteDesc' },
  { key: 'sms_interview_cancelled', label: 'smsPrefs.interviewCancelled', desc: 'smsPrefs.interviewCancelledDesc' },
  { key: 'sms_interview_rescheduled', label: 'smsPrefs.interviewRescheduled', desc: 'smsPrefs.interviewRescheduledDesc' },
  { key: 'sms_interview_reminder', label: 'smsPrefs.interviewReminder', desc: 'smsPrefs.interviewReminderDesc' },
  { key: 'sms_verification_approved', label: 'smsPrefs.verificationApproved', desc: 'smsPrefs.verificationApprovedDesc' },
];

const SmsNotificationSettings = ({ profile }) => {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  const isEmployer = profile?.role === 'employer' || profile?.role === 'owner';
  const prefList = isEmployer ? EMPLOYER_PREFS : WORKER_PREFS;

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await api.get('/api/profile/notification-preferences', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      setPrefs(res.data.preferences);
    } catch (err) {
      console.error('Failed to load SMS preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePref = async (key) => {
    const prev = { ...prefs };
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await api.put('/api/profile/notification-preferences', { [key]: updated[key] }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
    } catch (err) {
      setPrefs(prev);
      toast.error(t('smsPrefs.updateFailed'));
    }
  };

  if (loading || !prefs) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare size={20} />
          {t('smsPrefs.title')}
        </CardTitle>
        <CardDescription>
          {t('smsPrefs.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {prefList.map((item, i) => (
          <React.Fragment key={item.key}>
            {i > 0 && <Separator />}
            <div className="flex items-center justify-between">
              <Label htmlFor={item.key} className="flex-1 cursor-pointer pr-4">
                <div className="font-medium text-sm">{t(item.label)}</div>
                <div className="text-xs text-muted-foreground">{t(item.desc)}</div>
              </Label>
              <Switch
                id={item.key}
                checked={prefs[item.key] !== false}
                onCheckedChange={() => togglePref(item.key)}
              />
            </div>
          </React.Fragment>
        ))}
      </CardContent>
    </Card>
  );
};

export default SmsNotificationSettings;
